/**
 * MyTodoCard — canonical to-do surface on My Guru (Module 10 Addendum §1).
 *
 * Renders the authenticated user's active tasks (todo + in_progress),
 * sorted by the backend (overdue → due today → high → upcoming).
 * Includes quick complete/reopen + Add Task inline. The full task list
 * ("View All") is reachable by clicking through.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  Plus, CheckCircle2, Circle, PlayCircle, CalendarDays, Flag, Loader2,
  ClipboardCheck, MoreHorizontal, Trash2, Undo2,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'gleam_auth_token';

const PRIORITY_META = {
  high:   { label: 'High',   bg: 'bg-red-50',    text: 'text-red-700',    ring: 'ring-red-200'   },
  medium: { label: 'Medium', bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-200'},
  low:    { label: 'Low',    bg: 'bg-blue-50',   text: 'text-blue-700',   ring: 'ring-blue-200'  },
};

const dueLabel = (t) => {
  if (!t.due_date) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(t.due_date + 'T00:00:00');
  const diff = Math.round((d - today) / 86400000);
  if (t.is_overdue) return { text: 'Overdue', tone: 'text-red-600' };
  if (diff === 0)   return { text: 'Due Today', tone: 'text-[#F56F05]' };
  if (diff === 1)   return { text: 'Due Tomorrow', tone: 'text-zinc-600' };
  return { text: `Due ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, tone: 'text-zinc-600' };
};

// ---------------------------------------------------------------------------
// Compact task row
// ---------------------------------------------------------------------------
export const TaskRow = ({ task, canManage, onChange, onOpen, dense = false }) => {
  const [saving, setSaving] = useState(false);
  const p = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const due = dueLabel(task);
  const isDone = task.status === 'done';
  const isProg = task.status === 'in_progress';

  const patch = async (changes) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/api/tasks/${task.id}`, changes, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      onChange?.(r.data.task);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const cycleStatus = () => {
    if (isDone) patch({ status: 'todo' });
    else if (isProg) patch({ status: 'done' });
    else patch({ status: 'in_progress' });
  };

  return (
    <div
      data-testid={`task-row-${task.id}`}
      className={`group flex items-start gap-3 py-2.5 ${dense ? '' : 'border-b border-zinc-100 last:border-0'}`}
    >
      <button
        type="button"
        onClick={cycleStatus}
        disabled={saving}
        aria-label={isDone ? 'Reopen task' : isProg ? 'Mark complete' : 'Mark in progress'}
        data-testid={`task-toggle-${task.id}`}
        className="flex-shrink-0 mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F56F05] rounded-full"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
          : isDone ? <CheckCircle2 className="w-4 h-4 text-emerald-500 fill-emerald-500/20" />
          : isProg ? <PlayCircle className="w-4 h-4 text-[#F56F05]" />
          : <Circle className="w-4 h-4 text-zinc-400 hover:text-[#F56F05]" />}
      </button>

      <button type="button" onClick={() => onOpen?.(task)} className="flex-1 min-w-0 text-left">
        <p className={`text-[13px] font-semibold truncate ${isDone ? 'line-through text-zinc-400' : 'text-zinc-900'}`}>
          {task.title}
        </p>
        <div className="flex items-center gap-2 mt-0.5 text-[11px]">
          {due && <span className={due.tone}>{due.text}</span>}
          {isProg && <span className="text-[#F56F05] font-semibold">In Progress</span>}
          {task.link && <span className={`${task.link.href ? 'text-[#b75b28] font-semibold' : 'text-zinc-400'} truncate`} data-testid={`my-task-link-${task.id}`}>{task.link.label}</span>}
        </div>
      </button>

      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${p.bg} ${p.text}`}
        data-testid={`task-priority-${task.id}`}
      >
        <Flag className="w-2.5 h-2.5" />{p.label}
      </span>

      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded hover:bg-zinc-100" data-testid={`task-menu-${task.id}`}>
              <MoreHorizontal className="w-3.5 h-3.5 text-zinc-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onOpen?.(task)}>Edit</DropdownMenuItem>
            {isDone
              ? <DropdownMenuItem onClick={() => patch({ status: 'todo' })}><Undo2 className="w-3.5 h-3.5 mr-1.5" />Reopen</DropdownMenuItem>
              : <DropdownMenuItem onClick={() => patch({ status: 'done' })}>Mark Complete</DropdownMenuItem>}
            <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={async () => {
              try {
                await axios.delete(`${API}/api/tasks/${task.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
                onChange?.(null, task.id);
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to archive'); }
            }}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Archive</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add/Edit task dialog
// ---------------------------------------------------------------------------
export const TaskEditorDialog = ({ open, onClose, task, onSaved, forGuruUserId = null }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (open) {
      setForm(task ? {
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        due_date: task.due_date || '',
        status: task.status || 'todo',
      } : { title: '', description: '', priority: 'medium', due_date: '', status: 'todo' });
    }
  }, [open, task]);

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` };
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        due_date: form.due_date || null,
      };
      let r;
      if (task?.id) {
        r = await axios.patch(`${API}/api/tasks/${task.id}`, { ...payload, status: form.status }, { headers });
      } else if (forGuruUserId) {
        r = await axios.post(`${API}/api/admin/gurus/${forGuruUserId}/tasks`, payload, { headers });
      } else {
        r = await axios.post(`${API}/api/tasks/my`, payload, { headers });
      }
      toast.success(task?.id ? 'Task updated' : 'Task added');
      onSaved?.(r.data.task);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save task');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="task-editor-dialog">
        <DialogHeader>
          <DialogTitle>{task?.id ? 'Edit Task' : 'New Task'}</DialogTitle>
          <DialogDescription>
            {forGuruUserId ? 'Assign a task to this Guru.' : 'A quick to-do for yourself.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[12px] font-semibold">Title</Label>
            <Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="task-title-input" placeholder="Confirm Thursday walkthrough" />
          </div>
          <div>
            <Label className="text-[12px] font-semibold">Description <span className="text-zinc-400 font-normal">(optional)</span></Label>
            <Textarea rows={3} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="task-description-input" placeholder="Any notes or context…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-semibold">Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px] font-semibold">Due Date</Label>
              <Input type="date" value={form.due_date || ''} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-due-input" />
            </div>
          </div>
          {task?.id && (
            <div>
              <Label className="text-[12px] font-semibold">Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">To Do</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#F56F05] hover:bg-orange-600" data-testid="task-save-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// My To-Do card (5 items + Add Task)
// ---------------------------------------------------------------------------
export const MyTodoCard = ({ tasks, openCount, onChanged }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const openEditor = (t) => { setEditingTask(t); setEditorOpen(true); };
  const closeEditor = () => { setEditorOpen(false); setEditingTask(null); };

  return (
    <div data-testid="my-todo-card" className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-widest text-zinc-500 uppercase">
          <ClipboardCheck className="w-3.5 h-3.5 text-[#F56F05]" /> My To-Do
        </div>
        {openCount > 5 && (
          <span className="text-[11px] font-bold text-[#F56F05]" data-testid="todo-total-count">{openCount} open</span>
        )}
      </div>

      <div className="flex-1 min-h-[100px]">
        {!tasks || tasks.length === 0 ? (
          <div className="py-6 text-center text-zinc-500">
            <ClipboardCheck className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-[13px] font-semibold text-zinc-700">Nothing on the list.</p>
            <p className="text-[11px] text-zinc-500 mt-1">Add your first task to keep the day focused.</p>
          </div>
        ) : (
          <div>
            {tasks.map(t => (
              <TaskRow
                key={t.id}
                task={t}
                canManage
                dense
                onOpen={openEditor}
                onChange={(updated, deletedId) => onChanged?.(updated, deletedId)}
              />
            ))}
          </div>
        )}
      </div>

      <Button
        onClick={() => { setEditingTask(null); setEditorOpen(true); }}
        variant="outline"
        className="w-full mt-3 border-dashed"
        data-testid="add-task-btn"
      >
        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Task
      </Button>

      <TaskEditorDialog
        open={editorOpen}
        onClose={closeEditor}
        task={editingTask}
        onSaved={(t) => onChanged?.(t)}
      />
    </div>
  );
};
